import React, { useRef, createContext, useContext, ReactNode } from 'react';
import Reaptcha from 'reaptcha';

const SITE_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || '';

type RecaptchaContextType = {
  execute: () => Promise<string>;
};

const RecaptchaContext = createContext<RecaptchaContextType | null>(null);

export const RecaptchaProvider = ({ children }: { children: ReactNode }) => {
  const recaptchaRef = useRef<Reaptcha | null>(null);
  const resolveRef = useRef<((value: string) => void) | null>(null);

  // Skip reCAPTCHA in E2E test environment to prevent timeouts
  // Handle both string 'true' and boolean true from environment
  const isE2ETest =
    process.env.E2E_TEST_ENVIRONMENT === 'true' || (process.env.E2E_TEST_ENVIRONMENT as unknown) === true;

  const execute = (): Promise<string> =>
    new Promise((resolve) => {
      if (isE2ETest) {
        resolve('test-recaptcha-token');
        return;
      }

      resolveRef.current = resolve;
      recaptchaRef.current?.execute();
    });

  const onVerify = (token: string) => {
    if (resolveRef.current) {
      resolveRef.current(token);
      resolveRef.current = null;
      recaptchaRef.current?.reset();
    }
  };

  return (
    <RecaptchaContext.Provider value={{ execute }}>
      {/* Only render reCAPTCHA in non-test environments */}
      {!isE2ETest && (
        <div style={{ position: 'absolute', left: '-9999px' }}>
          <Reaptcha ref={recaptchaRef} size="invisible" sitekey={SITE_KEY} onVerify={onVerify} />
        </div>
      )}
      {children}
    </RecaptchaContext.Provider>
  );
};

export const useRecaptcha = () => {
  const context = useContext(RecaptchaContext);
  if (!context) {
    throw new Error('useRecaptcha must be used within RecaptchaProvider');
  }
  return context;
};
